import { useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  ManagementCategoryId,
  ManagementTaskAreaId,
  ResolvedManagementSectionEntry,
} from "../management-section-registry"

import { Check, ChevronsUpDown } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { AdminFocusedSurfaceContext } from "@/features/admin-dashboard/admin-focused-surface-context"
import { LockedManagementSection } from "@/features/admin-dashboard/components/locked-management-section"
import {
  CATEGORY_LABEL_DEFAULTS,
  CATEGORY_LABEL_KEYS,
} from "@/features/admin-dashboard/management-category-labels"
import {
  getManagementSectionsForArea,
  MANAGEMENT_CATEGORY_IDS,
  searchManagementSections,
} from "@/features/admin-dashboard/management-section-registry"
import { cn } from "@/lib/utils"

import { AdminNavigationGroup } from "./admin-navigation-group"

type AdminManagementWorkspaceProps = {
  previewError: boolean
  previewLoading: boolean
  sections: ResolvedManagementSectionEntry[]
  selectedSettingId?: string
  taskArea: ManagementTaskAreaId | "all"
  onSelectSetting: (section: ResolvedManagementSectionEntry) => void
}

export function AdminManagementWorkspace({
  previewError,
  previewLoading,
  sections,
  selectedSettingId,
  taskArea,
  onSelectSetting,
}: AdminManagementWorkspaceProps) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerQuery, setPickerQuery] = useState("")
  const pickerTriggerRef = useRef<HTMLButtonElement>(null)
  const getCategoryLabel = (category: ManagementCategoryId) =>
    t(CATEGORY_LABEL_KEYS[category], CATEGORY_LABEL_DEFAULTS[category])
  const getCategory = (section: ResolvedManagementSectionEntry): ManagementCategoryId =>
    section.taskArea === "access-audit" ? "access-audit" : (section.category ?? "product-features")
  const getTitle = (section: ResolvedManagementSectionEntry) =>
    section.titleFallback ? t(section.titleKey, section.titleFallback) : t(section.titleKey)

  const areaSections = useMemo(
    () => (taskArea === "all" ? sections : getManagementSectionsForArea(sections, taskArea)),
    [sections, taskArea]
  )
  const selectedSection =
    areaSections.find((section) => section.id === selectedSettingId) ?? areaSections.at(0)
  const pickerSections = searchManagementSections(areaSections, pickerQuery, getTitle)
  const visibleCategories = MANAGEMENT_CATEGORY_IDS.filter((category) =>
    areaSections.some((section) => getCategory(section) === category)
  )

  const handlePickerOpenChange = (open: boolean) => {
    setPickerOpen(open)
    if (!open) {
      setPickerQuery("")
      pickerTriggerRef.current?.focus()
    }
  }

  if (previewLoading) {
    return (
      <div aria-live="polite" className="text-muted-foreground rounded-lg border p-6 text-sm">
        {t(
          "adminDashboard.workspace.management.loading",
          "Loading the customer-admin capability matrix…"
        )}
      </div>
    )
  }

  if (previewError) {
    return (
      <div
        className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border p-6 text-sm"
        role="alert"
      >
        {t(
          "adminDashboard.workspace.management.loadError",
          "The customer-admin capability matrix could not be loaded. Settings remain hidden until it can be verified."
        )}
      </div>
    )
  }

  if (!selectedSection) {
    return (
      <div className="text-muted-foreground rounded-lg border p-8 text-center text-sm">
        {t("adminDashboard.workspace.management.empty", "No settings are available in this area.")}
      </div>
    )
  }

  const SelectedSection = selectedSection.component
  const panelContent =
    selectedSection.renderState === "upsell" ? (
      <LockedManagementSection
        icon={<selectedSection.icon className="size-5" />}
        id={selectedSection.anchorWrap ? undefined : selectedSection.id}
        title={getTitle(selectedSection)}
      />
    ) : (
      <SelectedSection />
    )

  return (
    <div
      className="grid min-w-0 gap-5 xl:grid-cols-[15rem_minmax(0,1fr)]"
      data-testid="admin-management-workspace"
    >
      <div className="xl:hidden">
        <Popover open={pickerOpen} onOpenChange={handlePickerOpenChange}>
          <span
            className="text-muted-foreground mb-1.5 block text-xs font-semibold tracking-wider uppercase"
            id="admin-setting-picker-label"
          >
            {t("adminDashboard.workspace.settingPicker.label", "Setting")}
          </span>
          <PopoverTrigger asChild>
            <Button
              ref={pickerTriggerRef}
              aria-expanded={pickerOpen}
              aria-labelledby="admin-setting-picker-label admin-setting-picker-value"
              className="min-h-11 w-full justify-between rounded-md px-3 font-normal"
              role="combobox"
              type="button"
              variant="outline"
            >
              <span className="truncate" id="admin-setting-picker-value">
                {getTitle(selectedSection)}
              </span>
              <ChevronsUpDown aria-hidden="true" className="text-muted-foreground size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[calc(100vw-2rem)] p-0 sm:w-[28rem]">
            <Command shouldFilter={false}>
              <CommandInput
                value={pickerQuery}
                aria-label={t(
                  "adminDashboard.workspace.settingPicker.searchAriaLabel",
                  "Search settings"
                )}
                placeholder={t(
                  "adminDashboard.workspace.settingPicker.searchPlaceholder",
                  "Search all settings…"
                )}
                onValueChange={setPickerQuery}
              />
              <CommandList>
                <CommandEmpty>
                  {t("adminDashboard.workspace.settingPicker.noResults", "No settings found.")}
                </CommandEmpty>
                <CommandGroup>
                  {pickerSections.map((section) => {
                    const Icon = section.icon
                    const title = getTitle(section)
                    return (
                      <CommandItem
                        key={section.id}
                        className="min-h-11 gap-3"
                        value={[title, ...(section.searchTerms ?? [])].join(" ")}
                        onSelect={() => {
                          onSelectSetting(section)
                          handlePickerOpenChange(false)
                        }}
                      >
                        <Icon aria-hidden="true" className="text-muted-foreground size-4" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{title}</span>
                          <span className="text-muted-foreground block truncate text-xs">
                            {getCategoryLabel(getCategory(section))}
                          </span>
                        </span>
                        <Check
                          aria-hidden="true"
                          className={cn(
                            "size-4",
                            section.id === selectedSection.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <nav
        aria-label={t("adminDashboard.workspace.settingsNavigation", "Settings")}
        className="border-border bg-card hidden self-start rounded-lg border p-2 xl:sticky xl:top-4 xl:block xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto"
      >
        {visibleCategories.map((category) => (
          <AdminNavigationGroup
            key={category}
            activeKey={getCategory(selectedSection) === category ? selectedSection.id : undefined}
            label={getCategoryLabel(category)}
            storageKey={`admin-settings-group-${category}`}
          >
            {areaSections
              .filter((section) => getCategory(section) === category)
              .map((section) => {
                const Icon = section.icon
                const selected = section.id === selectedSection.id
                return (
                  <button
                    key={section.id}
                    aria-current={selected ? "page" : undefined}
                    type="button"
                    className={cn(
                      "text-foreground hover:bg-accent focus-visible:ring-ring flex min-h-11 w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm focus-visible:ring-2 focus-visible:outline-none",
                      selected
                        ? "border-primary-foreground bg-primary text-primary-foreground font-semibold"
                        : "border-transparent"
                    )}
                    onClick={() => {
                      if (!selected) onSelectSetting(section)
                    }}
                  >
                    <Icon
                      aria-hidden="true"
                      className={cn(
                        "size-4 shrink-0",
                        selected ? "text-primary-foreground" : "text-muted-foreground"
                      )}
                    />
                    <span className="min-w-0 flex-1">{getTitle(section)}</span>
                  </button>
                )
              })}
          </AdminNavigationGroup>
        ))}
      </nav>

      <div
        key={selectedSection.id}
        className="min-w-0"
        data-testid={`admin-section-panel-${selectedSection.id}`}
      >
        <AdminFocusedSurfaceContext.Provider value>
          {panelContent}
        </AdminFocusedSurfaceContext.Provider>
      </div>
    </div>
  )
}
